/**
 * Position Grouping Types
 *
 * Groups belong to the user, not an account: a member is an instrument in a specific
 * account (`PositionGroupMember`), so one group can hold positions from several accounts
 * and venues, and the same symbol in two accounts is two independent members.
 */
import type { PositionGroupMember } from '@/types/generated/public_api'

export type GroupMode = 'none' | 'auto' | 'custom' | 'asset'

export interface PositionGroup {
  /** Unique identifier for the group */
  id: string
  /** Display name — editable by user */
  name: string
  /** Whether the user has manually renamed this group */
  isRenamed: boolean
  /** Members (account + instrument) that belong to this group, in leg order */
  members: PositionGroupMember[]
  /** Sub-groups nested inside this group */
  subGroups: PositionSubGroup[]
  /** Whether the group is collapsed in the UI */
  isCollapsed: boolean
}

export interface PositionSubGroup {
  /** Unique identifier for the sub-group */
  id: string
  /** Display name — editable by user */
  name: string
  /** Whether the user has manually renamed this sub-group */
  isRenamed: boolean
  /** Members (account + instrument) that belong to this sub-group, in leg order */
  members: PositionGroupMember[]
  /** Whether the sub-group is collapsed in the UI */
  isCollapsed: boolean
}

/**
 * A group as the grouped table renders it: each member resolved to the row key
 * (`getPositionRowKey`) of its live position — the same identity selection and drag use.
 * Members with no live position have no row and are left out.
 */
export interface RenderedPositionSubGroup extends Omit<PositionSubGroup, 'members'> {
  rowKeys: string[]
}

export interface RenderedPositionGroup extends Omit<PositionGroup, 'members' | 'subGroups'> {
  rowKeys: string[]
  subGroups: RenderedPositionSubGroup[]
}

export interface PositionGroupState {
  /** Current grouping mode */
  mode: GroupMode
  /** User-defined groups (only relevant in 'custom' mode) */
  groups: PositionGroup[]
}

export const DEFAULT_POSITION_GROUP_STATE: PositionGroupState = {
  mode: 'none',
  groups: [],
}
